import React from 'react'
import { useTranslation } from '../../../../../shared/hooks/useIntractionTranslate'
import { Activity } from '../../types'
import { INTERAX_COLORS, INTERAX_TYPOGRAPHY } from '../../../../../constants/colors'
import { ActivityCard } from '../ActivityCard'
import { createActivityInstanceFromTemplate, getTranslatedAvailableActivities } from '../../utils/activityConfiguration'

export interface AddActivitySectionProps {
  selectedActivities: Activity[]
  onAddActivity: (activity: Activity) => void
  availableActivities?: Activity[]
  className?: string
}

export const AddActivitySection: React.FC<AddActivitySectionProps> = ({
  selectedActivities,
  onAddActivity,
  availableActivities: availableActivitiesProp,
  className = ''
}) => {
  const { t } = useTranslation()
  const tCompat = t as (key: string, defaultValue?: string) => string
  const availableActivities = availableActivitiesProp ?? getTranslatedAvailableActivities(tCompat)

  // Get count of specific activity type in selected activities
  const getActivityCount = (activityType: string) => {
    return selectedActivities.filter((activity) => activity.type === activityType).length
  }

  // Check if activity can be added (respecting limits)
  const canAddActivity = (activity: Activity) => {
    return getActivityCount(activity.type) < (activity.limit || Infinity)
  }

  const handleActivityAdd = (activity: Activity) => {
    if (!canAddActivity(activity)) {
      return
    }

    const newActivity = createActivityInstanceFromTemplate(activity.type, tCompat)

    if (!newActivity) {
      return
    }

    onAddActivity(newActivity)
  }

  return (
    <div className={`space-y-6 ${className}`}>
      <div>
        <h3
          className="font-semibold mb-2"
          style={{
            color: INTERAX_COLORS.white,
            fontFamily: INTERAX_TYPOGRAPHY.headline3.fontFamily,
            fontWeight: INTERAX_TYPOGRAPHY.headline3.fontWeight,
            fontSize: INTERAX_TYPOGRAPHY.headline3.fontSize,
            lineHeight: INTERAX_TYPOGRAPHY.headline3.lineHeight
          }}
        >
          {t('facilitator.trainingSprint.contentStep.addActivity')}
        </h3>
        <p
          className="text-sm"
          style={{
            color: INTERAX_COLORS.typographyFont,
            fontFamily: INTERAX_TYPOGRAPHY.body3.fontFamily,
            fontWeight: INTERAX_TYPOGRAPHY.body3.fontWeight,
            fontSize: INTERAX_TYPOGRAPHY.body3.fontSize,
            lineHeight: INTERAX_TYPOGRAPHY.body3.lineHeight
          }}
        >
          {t('facilitator.trainingSprint.contentStep.addActivitySubtitle')}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {availableActivities.map((activity) => {
          const currentCount = getActivityCount(activity.type)
          const isLimitReached = activity.limit && currentCount >= activity.limit

          return (
            <ActivityCard
              key={activity.id}
              activity={activity}
              onAdd={() => handleActivityAdd(activity)}
              disabled={!!isLimitReached}
              currentCount={currentCount}
              limit={activity.limit}
            />
          )
        })}
      </div>
    </div>
  )
}
